import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

class LocalPickup extends Component {
  /** @type {AbortController | undefined} */
  #activeFetch;
  /** @type {(() => void) | undefined} */
  #removeVariantListener;

  connectedCallback() {
    super.connectedCallback();

    const closestSection = this.closest(`.shopify-section, dialog`);

    /** @type {(event: VariantUpdateEvent) => void} */
    const variantUpdated = (event) => {
      if (event.detail.data.newProduct) {
        this.dataset.productUrl = event.detail.data.newProduct.url;
      }

      const variantId = event.detail.resource?.id ? String(event.detail.resource.id) : '';
      const variantAvailable = event.detail.resource ? event.detail.resource.available : null;
      if (variantId !== this.dataset.variantId || !variantAvailable) {
        this.setAttribute('hidden', '');
        this.dataset.variantId = variantId;
        if (variantId && variantAvailable) {
          this.#fetchAvailability(variantId);
        } else {
          this.#activeFetch?.abort();
        }
      }
    };

    closestSection?.addEventListener(ThemeEvents.variantUpdate, variantUpdated);

    this.#removeVariantListener = () => {
      closestSection?.removeEventListener(ThemeEvents.variantUpdate, variantUpdated);
    };
  }

  disconnectedCallback() {
    this.#removeVariantListener?.();
    this.#activeFetch?.abort();
    super.disconnectedCallback();
  }

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  /**
   * Fetches the availability of a variant.
   * @param {string} variantId - The ID of the variant to fetch availability for.
   */
  #fetchAvailability = (variantId) => {
    if (!variantId) return;

    const abortController = this.#createAbortController();

    const url = this.dataset.productUrl;
    fetch(`${url}?variant=${variantId}&section_id=${this.dataset.sectionId}`, {
      signal: abortController.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load pickup availability');
        return response.text();
      })
      .then((text) => {
        if (abortController.signal.aborted || this.dataset.variantId !== variantId) return;

        const html = new DOMParser().parseFromString(text, 'text/html');
        const wrapper = html.querySelector(`local-pickup[data-variant-id="${variantId}"]`);
        if (wrapper) {
          const shouldHide = wrapper.hasAttribute('hidden') || !wrapper.querySelector('.pickup-location__wrapper');
          morph(this, wrapper);
          // morph() updates children only; explicitly respect the wrapper state.
          this.toggleAttribute('hidden', shouldHide);
        } else this.setAttribute('hidden', '');
      })
      .catch((_e) => {
        if (abortController.signal.aborted) return;
        this.setAttribute('hidden', '');
      });
  };
}

if (!customElements.get('local-pickup')) {
  customElements.define('local-pickup', LocalPickup);
}
